import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Swiper from "swiper/bundle";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";

import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import SmartDPHeroCommercial from "app/pages/SmartDP/SmartDPHeroCommercial/SmartDPHeroCommercial";
import { executeHeroCommercialCta } from "app/pages/SmartDP/SmartDPHeroCommercial/SmartDPHeroCommercialContent";
import { useAnalytics } from "app/utils/analytics/useAnalytics";

import "./SmartDPHeroCommercialCarousel.scss";
import {
	ArrowLeftIcon,
	ArrowRightIcon,
	PauseFilledIcon,
	PlayFilledIcon,
} from "app/icons/navigation";

const SWIPER_DUPLICATE_SLIDE_CLASS = "swiper-slide-duplicate";
const HERO_COMMERCIAL_CTA_DELEGATION_SELECTOR =
	".smartDP-hero-commercial__redirect--button, .smartdp-marketing-headline, .smartDP-hero-commercial__logo-homepage";

const SmartDPHeroCommercialCarousel = ({
	merchandisings = [],
	autoplayDelay = 7000,
	className,
	destinations = [],
}) => {
	const { resolution } = useContext(AppGlobalsContext);
	const { track } = useAnalytics();
	const navigate = useNavigate();

	const isMobile = resolution === RESOLUTION.SMALL;

	const swiperElRef = useRef(null);
	const swiperRef = useRef(null);
	const merchandisingsRef = useRef(merchandisings);
	const navigateRef = useRef(navigate);
	const holdStateRef = useRef({ active: false, wasPlaying: false });
	const isMobileRef = useRef(isMobile);
	const trackRef = useRef(track);
	const swipeStateRef = useRef({
		started: false,
		moved: false,
		fromIndex: 0,
		direction: null,
	});
	const cycleStartedAtRef = useRef(Date.now());
	const cycleDelayRef = useRef(autoplayDelay);
	const remainingDelayRef = useRef(autoplayDelay);

	const [activeIndex, setActiveIndex] = useState(0);
	const [isPlaying, setIsPlaying] = useState(true);

	const totalSlides = merchandisings.length;

	useEffect(() => {
		isMobileRef.current = isMobile;
	}, [isMobile]);

	useEffect(() => {
		trackRef.current = track;
	}, [track]);

	useEffect(() => {
		merchandisingsRef.current = merchandisings;
	}, [merchandisings]);

	useEffect(() => {
		navigateRef.current = navigate;
	}, [navigate]);

	useEffect(() => {
		if (!swiperElRef.current || totalSlides === 0) {
			return undefined;
		}

		const swiperHost = swiperElRef.current;

		// Swiper loop mode duplicates slides with DOM cloneNode(). Those nodes are not React-managed, so
		// onClick handlers from SmartDPHeroCommercialContent do not run on duplicates after a full loop.
		// We attach a native capture listener on the swiper container, gate on .swiper-slide-duplicate only,
		// so real slides still use React handlers and we avoid double navigation / double analytics.
		const onDuplicateSlideCtaClickCapture = event => {
			const slide = event.target.closest?.(".swiper-slide");
			if (!slide || !slide.classList.contains(SWIPER_DUPLICATE_SLIDE_CLASS)) {
				return;
			}
			const interactive = event.target.closest?.(HERO_COMMERCIAL_CTA_DELEGATION_SELECTOR);
			if (!interactive || !slide.contains(interactive)) {
				return;
			}
			const idxAttr = slide.getAttribute("data-swiper-slide-index");
			const idx = idxAttr === null ? NaN : parseInt(idxAttr, 10);
			const list = merchandisingsRef.current;
			if (!Number.isInteger(idx) || idx < 0 || idx >= list.length) {
				return;
			}
			event.preventDefault();
			event.stopPropagation();
			executeHeroCommercialCta({
				heroCommercialMerch: list[idx],
				navigate: navigateRef.current,
				track: trackRef.current,
			});
		};

		swiperHost.addEventListener("click", onDuplicateSlideCtaClickCapture, true);

		swiperRef.current = new Swiper(swiperElRef.current, {
			speed: 700,
			loop: true,
			slidesPerView: 1,
			spaceBetween: 0,
			threshold: 5,
			shortSwipes: true,
			autoplay: {
				delay: autoplayDelay,
				disableOnInteraction: false,
			},
			on: {
				touchStart: function (swiper) {
					if (!isMobileRef.current) {
						return;
					}
					swipeStateRef.current.started = true;
					swipeStateRef.current.moved = false;
					swipeStateRef.current.direction = null;
					swipeStateRef.current.fromIndex = swiper.realIndex ?? swiper.activeIndex ?? 0;
				},
				sliderMove: function () {
					if (!isMobileRef.current) {
						return;
					}
					if (swipeStateRef.current.started) {
						swipeStateRef.current.moved = true;
					}
				},
				touchEnd: function (swiper) {
					if (!isMobileRef.current) {
						return;
					}
					if (swipeStateRef.current.started && swipeStateRef.current.moved) {
						swipeStateRef.current.direction = swiper.swipeDirection || null;
					}
				},
				slideChangeTransitionEnd: function (swiper) {
					if (!isMobileRef.current) {
						return;
					}
					const { started, moved, direction, fromIndex } = swipeStateRef.current;
					const toIndex = swiper.realIndex ?? swiper.activeIndex ?? 0;

					if (started && moved && direction && fromIndex !== toIndex) {
						trackRef.current("hero-commercial-carousel-swipe-mobile", {
							direction,
							fromIndex,
							toIndex,
						});
					}
					swipeStateRef.current.started = false;
					swipeStateRef.current.moved = false;
					swipeStateRef.current.direction = null;
				},
				slideChange: function (swiper) {
					setActiveIndex(swiper.realIndex ?? swiper.activeIndex ?? 0);

					// reset the autoplay cycle timing for indicator + pause/resume logic
					cycleStartedAtRef.current = Date.now();
					cycleDelayRef.current = autoplayDelay;
					remainingDelayRef.current = autoplayDelay;
					if (swiper.params?.autoplay) {
						swiper.params.autoplay.delay = autoplayDelay;
					}
				},
			},
		});

		cycleStartedAtRef.current = Date.now();
		cycleDelayRef.current = autoplayDelay;
		remainingDelayRef.current = autoplayDelay;

		return () => {
			swiperHost.removeEventListener("click", onDuplicateSlideCtaClickCapture, true);
			try {
				if (swiperRef.current) {
					swiperRef.current.destroy();
				}
			} catch (e) {
				// avoid breaking unmount if Swiper is already destroyed
			}
			swiperRef.current = null;
		};
	}, [autoplayDelay, totalSlides]);

	useEffect(() => {
		if (!swiperRef.current?.autoplay) {
			return;
		}

		if (isPlaying) {
			const delayToUse = Math.max(0, remainingDelayRef.current ?? autoplayDelay);
			cycleStartedAtRef.current = Date.now();
			cycleDelayRef.current = delayToUse;
			if (swiperRef.current.params?.autoplay) {
				swiperRef.current.params.autoplay.delay = delayToUse;
			}
			swiperRef.current.autoplay.start();
		} else {
			swiperRef.current.autoplay.stop();
		}
	}, [autoplayDelay, isPlaying]);

	const computeRemainingDelay = useCallback(() => {
		const elapsed = Date.now() - cycleStartedAtRef.current;
		remainingDelayRef.current = Math.max(0, cycleDelayRef.current - elapsed);
	}, []);

	const handleTogglePlay = useCallback(() => {
		setIsPlaying(prev => {
			const next = !prev;
			if (prev && !next) {
				// from play -> pause
				computeRemainingDelay();
				track("hero-commercial-carousel-pause-click");
			} else if (!prev && next) {
				// from pause -> play
				track("hero-commercial-carousel-play-click");
			}
			return next;
		});
	}, [computeRemainingDelay]);

	const handlePrev = useCallback(() => {
		track("hero-commercial-carousel-prev-click");
		if (swiperRef.current) {
			swiperRef.current.slidePrev();
		}
	}, []);

	const handleNext = useCallback(() => {
		track("hero-commercial-carousel-next-click");
		if (swiperRef.current) {
			swiperRef.current.slideNext();
		}
	}, []);

	const handleGoToSlide = useCallback(index => {
		track("hero-commercial-carousel-go-to-slide-click", { index });
		if (!swiperRef.current) {
			return;
		}
		swiperRef.current.slideToLoop(index);
	}, []);

	const handleNextMouseEnter = useCallback(() => {
		track("hero-commercial-carousel-next-mouse-hover");
	}, []);

	const handlePrevMouseEnter = useCallback(() => {
		track("hero-commercial-carousel-prev-mouse-hover");
	}, []);

	const handleContextMenu = useCallback(e => {
		// prevent mobile long-press context menu
		e.preventDefault();
	}, []);

	const handleTouchStart = useCallback(() => {
		if (!isMobile) {
			return;
		}

		holdStateRef.current.active = true;
		holdStateRef.current.wasPlaying = isPlaying;
		holdStateRef.current.startedAt = Date.now();
		holdStateRef.current.moved = false;

		if (isPlaying) {
			computeRemainingDelay();
		}

		setIsPlaying(false);
		if (swiperRef.current?.autoplay && typeof swiperRef.current.autoplay.stop === "function") {
			swiperRef.current.autoplay.stop();
		}
	}, [computeRemainingDelay, isMobile, isPlaying]);

	const handleTouchMove = useCallback(() => {
		if (!isMobile || !holdStateRef.current.active) {
			return;
		}
		holdStateRef.current.moved = true;
	}, [isMobile]);

	const handleTouchEnd = useCallback(() => {
		if (!isMobile || !holdStateRef.current.active) {
			return;
		}

		holdStateRef.current.active = false;

		if (holdStateRef.current.wasPlaying) {
			setIsPlaying(true);
		}

		const pressDurationMs = Date.now() - (holdStateRef.current.startedAt || Date.now());
		const isLongPress = pressDurationMs >= 500 && !holdStateRef.current.moved;
		if (isLongPress) {
			track("hero-commercial-carousel-long-press-mobile");
		}
	}, [isMobile]);

	const sliderStyle = useMemo(
		() => ({
			"--hcc-autoplay-delay": `${autoplayDelay}ms`,
		}),
		[autoplayDelay]
	);

	return (
		<div
			className={classNames("smartDP-hero-commercial-carousel", className)}
			data-testid="smartDPhero-commercial-carousel"
		>
			{!isMobile && (
				<>
					<button
						type="button"
						className="smartDP-hero-commercial-carousel__nav-button smartDP-hero-commercial-carousel__nav-button--prev flip-if-rlt"
						onClick={handlePrev}
						aria-label="Previous slide"
					>
						<ArrowLeftIcon
							className="smartDP-hero-commercial-carousel__nav-icon"
							onMouseEnter={handlePrevMouseEnter}
						/>
					</button>

					<button
						type="button"
						className="smartDP-hero-commercial-carousel__nav-button smartDP-hero-commercial-carousel__nav-button--next flip-if-rlt"
						onClick={handleNext}
						aria-label="Next slide"
					>
						<ArrowRightIcon
							className="smartDP-hero-commercial-carousel__nav-icon"
							onMouseEnter={handleNextMouseEnter}
						/>
					</button>
				</>
			)}
			<div
				ref={swiperElRef}
				className="swiper-container smartDP-hero-commercial-carousel__swiper"
				onContextMenu={handleContextMenu}
				{...(isMobile && {
					onTouchStart: handleTouchStart,
					onTouchMove: handleTouchMove,
					onTouchEnd: handleTouchEnd,
					onTouchCancel: handleTouchEnd,
				})}
			>
				<div className="swiper-wrapper">
					{merchandisings.map((merch, index) => (
						<div
							className="swiper-slide"
							key={merch?.code || merch?.id || merch?.title}
						>
							<SmartDPHeroCommercial
								heroCommercialMerch={merch}
								useSimpleBackgroundImage
								fetchPriority={index === 0 ? "high" : "auto"}
								destinations={destinations}
							/>
						</div>
					))}
				</div>
			</div>

			{totalSlides > 1 && (
				<div className="smartDP-hero-commercial-carousel__controls" style={sliderStyle}>
					<div className="smartDP-hero-commercial-carousel__slider">
						{!isMobile && (
							<button
								type="button"
								className="smartDP-hero-commercial-carousel__control"
								onClick={handleTogglePlay}
								aria-pressed={!isPlaying}
								aria-label={isPlaying ? "Pause carousel" : "Play carousel"}
							>
								{isPlaying ? (
									<PauseFilledIcon className="smartDP-hero-commercial-carousel__control-icon" />
								) : (
									<PlayFilledIcon className="smartDP-hero-commercial-carousel__control-icon" />
								)}
							</button>
						)}

						<div
							className="smartDP-hero-commercial-carousel__indicators"
							aria-hidden="true"
						>
							{Array.from({ length: totalSlides }).map((_, index) => (
								<span
									key={`indicator-${index}`}
									className={classNames(
										"smartDP-hero-commercial-carousel__indicator",
										{
											"smartDP-hero-commercial-carousel__indicator--active":
												index === activeIndex,
											"smartDP-hero-commercial-carousel__indicator--paused":
												!isPlaying,
										}
									)}
									onClick={() => handleGoToSlide(index)}
								>
									<span
										key={`fill-${index}-${activeIndex}`}
										className="smartDP-hero-commercial-carousel__indicator-fill"
									/>
								</span>
							))}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

SmartDPHeroCommercialCarousel.propTypes = {
	merchandisings: PropTypes.arrayOf(PropTypes.object),
	autoplayDelay: PropTypes.number,
	fetchPriority: PropTypes.oneOf(["auto", "high", "low"]),
	className: PropTypes.string,
	destinations: PropTypes.array,
};

export default memo(SmartDPHeroCommercialCarousel);
